import { useReducer, useEffect } from 'react'
import _kebabCase from 'lodash/kebabCase.js'
import { fetcher } from '../../productDetail/Helper.js'
const lookupCategory = [
  'Domestic Equity',
  'Global/International Equity',
  'Sector Equity/Specialty',
  'Asset Allocation',
  'Alternative',
  'Taxable Fixed Income',
  'Tax-Exempt Fixed Income',
  'Money Market',
]

export const productTypes = {
  MF: 'Retail',
  ETF: 'RetailETF',
  MP: 'ModelPortfolios',
  FS: 'FutureScholar529',
  VP: 'VariableProducts',
  IF: 'IntervalFund',
  CE: 'ClosedEnd',
}

const ACTIONS = {
  UPDATE_DATA: 'UPDATE_DATA',
  SORT_DATA: 'SORT_DATA',
}

export const productsConfig = {
  [productTypes.MF]: {
    label: 'Mutual Fund',
    value: 'mutual-funds',
  },
  [productTypes.ETF]: {
    label: 'Exchange Traded Fund (ETFs)',
    value: 'exchange-traded-funds',
  },
  [productTypes.MP]: {
    label: 'Model Portfolios',
    value: 'model-portfolios',
  },
  [productTypes.FS]: {
    label: '529 Plan',
    value: '529-plan',
  },
  [productTypes.CE]: {
    label: 'Listed Closed-End Funds',
    value: 'closed-end-funds',
  },
  [productTypes.VP]: {
    label: 'Variable Products',
    value: 'variable-products',
  },
  [productTypes.IF]: {
    label: 'Interval Funds',
    value: 'interval-funds',
  },
}

const initState = {
  isLoading: false,
  searchInput: '',
  rowsData: [],
  classOptions: {},
  assertsOptions: lookupCategory.reduce(
    (p, n) => ({ ...p, [_kebabCase(n)]: n }),
    {}
  ),
  productOptions: Object.values(productTypes).reduce(
    (p, n) => ({ ...p, [productsConfig[n].value]: productsConfig[n].label }),
    {}
  ),
  selected: {
    assetClass: [],
    productType: [],
    class: [],
  },
  selectedSort: 'fundName',
  selectedSortDirection: true,
  selectedSortType: 'string',
}

const getProductData = async (product) => {
  const url = `/pnp.svc/productList?group=${product}&groupbycategory=true`
  const data = (await fetcher(url))?.value || {}
  return data?.products?.reduce(
    (p, n) => {
      return n?.funds.reduce(
        (fp, fn) => {
          const { classes, fundName, subCtgValue, ctgName } = fn
          return classes.reduce(
            (cp, cn) => {
              const { fundClass, cusip, symbol } = cn
              const fnclass = fundClass
                ?.replace(/Class/g, '')
                ?.replace(/\s/g, '')
              if (fundClass) {
                cp.classOptions[fnclass?.toLowerCase()] = fnclass
              }
              cp?.rowsData?.push({
                productType: product,
                fundType: productsConfig[product].value,
                fundName,
                ctgName: _kebabCase(ctgName),
                assetClass: lookupCategory.includes(ctgName) ? ctgName : '-',
                cusip,
                fundClass: fnclass,
                class: fnclass?.toLowerCase(),
                symbol,
                subCtgValue: [productTypes.MF, productTypes.ETF].includes(
                  product
                )
                  ? subCtgValue
                    ? subCtgValue
                    : '-'
                  : '-',
              })
              return cp
            },
            { ...fp }
          )
        },
        { ...p }
      )
    },
    {
      rowsData: [],
      classOptions: {},
      assertsOptions: {},
    }
  )
}

const getAllProductData = async (products) => {
  const productsArray = products?.map((itm) => getProductData(itm))
  const data = await Promise.all(productsArray)
  return data.reduce(
    (p, n) => ({
      rowsData: [...p.rowsData, ...n.rowsData],
      classOptions: { ...p?.classOptions, ...n?.classOptions },
      assertsOptions: { ...p?.assertsOptions, ...n?.assertsOptions },
    }),
    {
      rowsData: [],
      classOptions: {},
      assertsOptions: {},
    }
  )
}

function combineReducer(state, action) {
  switch (action.type) {
    case ACTIONS.UPDATE_DATA: {
      const { rowsData, classOptions, assertsOptions, isLoading } =
        action.payload
      return {
        ...state,
        isLoading,
        rowsData: [...state.rowsData, ...rowsData],
        classOptions: { ...state.classOptions, ...classOptions },
        assertsOptions: { ...state.assertsOptions, ...assertsOptions },
      }
    }
    case ACTIONS.SORT_DATA: {
      return {
        ...state,
        ...action.payload,
      }
    }
    default:
      return state
  }
}

export default function useCombineMorningstarfundsReducer(urlParams) {
  const { MF, ...restProducts } = productTypes
  const [state, dispatch] = useReducer(combineReducer, initState)
  const { selectedSort, selectedSortDirection } = state

  const initialLoad = async () => {
    dispatch({
      type: ACTIONS.SORT_DATA,
      payload: { isLoading: true },
    })

    const firstProductData = await getProductData(MF)
    dispatch({
      type: ACTIONS.UPDATE_DATA,
      payload: {
        isLoading: false,
        ...firstProductData,
      },
    })
    const remainingProductData = await getAllProductData(
      Object.values(restProducts)
    )
    dispatch({
      type: ACTIONS.UPDATE_DATA,
      payload: {
        isLoading: false,
        ...remainingProductData,
      },
    })
  }

  const sortData = (payload = {}) => {
    dispatch({ type: ACTIONS.SORT_DATA, payload })
  }

  const onClickSort = (event) => {
    const sort = event.currentTarget.dataset.sort
    const sortType = event.currentTarget.dataset.sortType
    const sortDirection = !!parseInt(event.currentTarget.dataset.sortDirection)
    sortData({ selectedSortType: sortType })
    if (sort === selectedSort) {
      sortData({ selectedSortDirection: !selectedSortDirection })
    } else {
      sortData({
        selectedSort: sort,
        selectedSortDirection: sortDirection,
      })
    }
  }

  useEffect(() => {
    if (Object.keys(state.classOptions).length > 0) {
      getUrlParams(state, sortData, urlParams)
    }
  }, [state.classOptions])

  useEffect(() => {
    initialLoad()
  }, [])

  return {
    ...state,
    onClickSort,
    sortData,
    initialLoad,
  }
}

export const filterData = (selected, searchInput, data) => {
  const count = Object.keys(selected).reduce(
    (p, n) => p + selected[n]?.length,
    0
  )
  let result = data

  if (count > 0) {
    if (selected?.productType?.length > 0) {
      result = data?.filter((i) => selected?.productType?.includes(i?.fundType))
    }
    if (selected?.assetClass?.length > 0) {
      result = result?.filter((i) => selected?.assetClass?.includes(i?.ctgName))
    }
    if (selected?.class?.length > 0) {
      result = result?.filter((i) => selected?.class?.includes(i?.class))
    }
  }

  if (searchInput?.length > 0) {
    result = result?.filter((i) =>
      `${i?.symbol} ${i?.fundName}`
        ?.toLowerCase()
        ?.includes(searchInput?.toLowerCase())
    )
  }

  return result
}

export const getUrlParams = (
  { classOptions, assertsOptions, productOptions },
  sortData,
  urlParams
) => {
  if (
    urlParams.has('productType') ||
    urlParams.has('assetClass') ||
    urlParams.has('class')
  ) {
    let _product = [],
      _class = [],
      _asserts = []
    if (urlParams.has('productType')) {
      _product = urlParams
        ?.get('productType')
        ?.split(',')
        ?.filter((i) => productOptions[i])
    }

    if (urlParams.has('assetClass')) {
      _asserts = urlParams
        ?.get('assetClass')
        ?.split(',')
        ?.filter((i) => assertsOptions[i])
    }

    if (urlParams.has('class')) {
      _class = urlParams
        ?.get('class')
        ?.split(',')
        ?.filter((i) => classOptions[i])
    }
    sortData({
      selected: {
        assetClass: _asserts,
        productType: _product,
        class: _class,
      },
    })
  }
}

export const updateUrlParams = (name, value) => {
  const url = new URL(window.location.href)
  const params = url?.searchParams
  if (value === undefined || value?.length < 1) {
    params?.delete(name)
  } else {
    const _val = value?.toString()
    params?.set(name, _val)
  }
  history?.replaceState({}, '', url.toString())
}
